import { getSupabase as getSupabaseNullable } from "../../../lib/supabase";
import type { IOperationResult } from "../../types";
import type { SystemSetting } from "../../../types/admin";
import { fromDb } from "../baseService";

function requireSupabase() {
  const client = getSupabaseNullable();
  if (!client) throw new Error("Supabase not initialized. Call initSupabase() first.");
  return client;
}

/**
 * Custom service for system_settings table.
 * Uses `key` as primary key instead of `id`.
 */
export class AdminSystemSettingsService {
  static async getAll(): Promise<IOperationResult<SystemSetting[]>> {
    try {
      const supabase = requireSupabase();
      const { data, error } = await supabase
        .from("system_settings")
        .select("*")
        .order("key", { ascending: true });

      if (error) return { success: false, error };

      const mapped = (data ?? []).map((row) =>
        fromDb<SystemSetting>(row as Record<string, unknown>)
      );
      return { success: true, data: mapped };
    } catch (error) {
      return { success: false, error };
    }
  }

  static async getByKey(key: string): Promise<IOperationResult<SystemSetting>> {
    try {
      const supabase = requireSupabase();
      const { data, error } = await supabase
        .from("system_settings")
        .select("*")
        .eq("key", key)
        .single();

      if (error) return { success: false, error };
      return {
        success: true,
        data: fromDb<SystemSetting>(data as Record<string, unknown>),
      };
    } catch (error) {
      return { success: false, error };
    }
  }

  static async upsert(
    key: string,
    value: unknown,
    description?: string
  ): Promise<IOperationResult<SystemSetting>> {
    try {
      const supabase = requireSupabase();

      const record: Record<string, unknown> = {
        key,
        value: JSON.stringify(value),
        updated_at: new Date().toISOString(),
      };

      if (description !== undefined) {
        record.description = description;
      }

      // Get current user for updated_by
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user) {
        record.updated_by = user.id;
      }

      const { data, error } = await supabase
        .from("system_settings")
        .upsert(record, { onConflict: "key" })
        .select()
        .single();

      if (error) return { success: false, error };
      return {
        success: true,
        data: fromDb<SystemSetting>(data as Record<string, unknown>),
      };
    } catch (error) {
      return { success: false, error };
    }
  }

  static async delete(key: string): Promise<void> {
    const supabase = requireSupabase();
    await supabase.from("system_settings").delete().eq("key", key);
  }
}
